/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, TextField } from "@mui/material";

import useStyle from "./styles";
import { MESSAGES, TITLE } from "./constants";
import Loader from "../../shared/loader/Loader";
import FormDialog from "../../shared/dialog/FormDialog";
import { selectError } from "../../store/error/errorSelector";
import { ErrorMessage } from "../../shared/ErrorMessage/ErrorMessage";
import { CustomTooltip } from "../../shared/customTooltip/CustomTooltip";
import { selectSelectedOrganization } from "../../store/organization/organizationSelector";
import { createOrganization, editOrganization } from "../../store/organization/organizations";
import { hideLoaderAction } from "../../store/loader/loaderActionCreator";
import { showErrorAction } from "../../store/error/errorActionCreator";

export interface IOrganizationData {
  id?: string;
  name?: string;
}

interface IAddOrganizationDialogProps {
  organizationData?: IOrganizationData;
  isEdit: boolean;
  openCloseModal: React.Dispatch<React.SetStateAction<boolean>>;
  openModal: boolean;
  refreshOnSave: Function;
}

export interface IOrganizationRequest {
  name?: string;
}

const AddOrganizationDialog = (props: IAddOrganizationDialogProps) => {
  const { openModal, openCloseModal } = props;
  const classes = useStyle();
  const dispatch = useDispatch();

  const formDefaultValues: IOrganizationRequest = {
    name: "",
  };
  const formErrorDefaultValues = {
    error: false,
    errorHelperText: "",
  };

  const errorMesaage = useSelector(selectError);
  const selectedOrganization = useSelector(selectSelectedOrganization)

  const [showLoader, showHideLoader] = useState(false);
  const [formErrorState, updateFormError] = useState(formErrorDefaultValues);
  const [formValues, updateFormValues] = useState<IOrganizationRequest>(formDefaultValues);

  useEffect(() => {
    updateFormValues({ ...formDefaultValues });
    updateFormError({ ...formErrorDefaultValues });
    dispatch(hideLoaderAction());
    dispatch(showErrorAction({ message: '' }))
  }, [props.openModal]);

  useEffect(() => {
    updateFormValues({ ...formDefaultValues });
    showHideLoader(false);
    if (openModal && props.isEdit) {
      updateFormValues({ ...props.organizationData });
    }
  }, [openModal, props.isEdit, props.organizationData]);

  const handleClose = () => {
    openCloseModal(!openModal);
  };

  const updateFormFields = (event: { target: { name: string; value: string } }) => {
    let targetValue = event.target.value;
    updateFormValues({ ...formValues, name: targetValue });
    updateFormError({ ...formErrorState, error: false });
  };

  const validateRequired = (values: any) => {
    if (!values.name) return false;
    else return true;
  };

  const handleSubmit = async () => {
    let isSuccess = false;
    if (validateRequired(formValues)) {
      const payload: any = {
        name: formValues.name
      }
      showHideLoader(true);
      if (props.isEdit) {
        payload.id = selectedOrganization?.id;
        isSuccess = await editOrganization(dispatch, payload);
      } else {
        isSuccess = await createOrganization(dispatch, payload);
      }
      if (isSuccess) handleClose();
      showHideLoader(false);
    }
  };

  return (
    <Fragment>
      <FormDialog
        dialogAction1={
          <Button
            color="primary"
            disableElevation
            name="cancelBtn"
            variant="outlined"
            onClick={handleClose}
          >
            {TITLE.CANCEL}
          </Button>
        }
        dialogAction2={
          <Button
            color="primary"
            disableElevation
            variant="contained"
            disabled={showLoader}
            onClick={handleSubmit}
            name="createCustomerBtn"
          >
            {TITLE.SAVE}
          </Button>
        }
        openModal={openModal}
        handleClose={handleClose}
        dialogTitle={props.isEdit ? TITLE.EDIT_ORGANIZATION : TITLE.ADD_ORGANIZATION}
      >
        <Box className={classes.modalFormContent}>
          <TextField
            required
            fullWidth
            id="name"
            name="displayName"
            variant="standard"
            disabled={showLoader}
            value={formValues.name}
            onChange={updateFormFields}
            error={formErrorState.error}
            label={TITLE.ORGANIZATION_NAME}
            className={classes.modalTextBox}
            helperText={formErrorState.errorHelperText}
          />
          <CustomTooltip title={MESSAGES.ORGANIZATION_NAME_INFO}></CustomTooltip>
        </Box>
        <Loader showLoader={showLoader} isCenter />
        {errorMesaage && <ErrorMessage message={errorMesaage.message} />}
      </FormDialog>
    </Fragment>
  );
};

export default AddOrganizationDialog;
